import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { DateInput } from "./ui/date-input";
import { fmtDateIN } from "../lib/utils";
import { CaretUpDown, Check, UserPlus, SignOut } from "@phosphor-icons/react";
import { toast } from "sonner";

export function EmployeePicker({ options, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const cur = options.find((o) => o.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="w-full h-8 border border-zinc-300 rounded px-2 text-sm flex items-center justify-between bg-white" data-testid={testid}>
          <span className={cur ? "" : "text-zinc-500"}>{cur ? cur.label : "— Skip this employee —"}</span>
          <CaretUpDown size={14} className="text-zinc-400"/>
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[420px]" align="start">
        <Command>
          <CommandInput placeholder="Type a name or employee ID…" data-testid={`${testid}-search`}/>
          <CommandList className="max-h-72">
            <CommandEmpty>No employee found.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__skip" onSelect={() => { onChange(""); setOpen(false); }}>— Skip this employee —</CommandItem>
              {options.map((o) => (
                <CommandItem key={o.id} value={`${o.label} ${o.id}`} onSelect={() => { onChange(o.id); setOpen(false); }} data-testid={`${testid}-opt-${o.code}`}>
                  <Check size={12} className={`mr-2 ${o.id === value ? "opacity-100" : "opacity-0"}`}/>{o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function QuickAddEmployeeDialog({ row, onClose, onAdded }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  if (row && (!f || f._bid !== row.biometric_id)) {
    setF({ _bid: row.biometric_id, name: row.biometric_name || "", employee_code: row.biometric_id, date_of_joining: row.first_punch || "",
      job_title: "Staff", monthly_salary: "", payment_category: "cash", phone: "" });
  }
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    if (!(+f.monthly_salary > 0)) return toast.error("Enter the monthly salary");
    setBusy(true);
    try {
      const { data } = await api.post("/attendance/biometric/quick-add-employee", { ...f, biometric_id: row.biometric_id, monthly_salary: +f.monthly_salary });
      toast.success(`${data.name} added`);
      onAdded(row.biometric_id, data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="bio-quick-add-dialog">
        <DialogHeader>
          <DialogTitle>Add new employee</DialogTitle>
          <DialogDescription>From device row #{row?.biometric_id}. Their punches are included as soon as you save.</DialogDescription>
        </DialogHeader>
        {f && <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="text-xs">Full name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="bio-qa-name"/></div>
          <div><Label className="text-xs">Employee ID</Label><Input value={f.employee_code} onChange={(e) => set("employee_code", e.target.value)} data-testid="bio-qa-code"/></div>
          <div><Label className="text-xs">Joining date</Label><DateInput value={f.date_of_joining} onChange={(e) => set("date_of_joining", e.target.value)} data-testid="bio-qa-doj"/></div>
          <div><Label className="text-xs">Designation</Label><Input value={f.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="bio-qa-title"/></div>
          <div><Label className="text-xs">Mobile</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="bio-qa-phone"/></div>
          <div><Label className="text-xs">Monthly salary (₹)</Label><Input type="number" value={f.monthly_salary} onChange={(e) => set("monthly_salary", e.target.value)} data-testid="bio-qa-salary"/></div>
          <div><Label className="text-xs">Paid by</Label>
            <Select value={f.payment_category} onValueChange={(v) => set("payment_category", v)}>
              <SelectTrigger data-testid="bio-qa-paymode"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="idbi">IDBI bank</SelectItem><SelectItem value="non_idbi">Other bank</SelectItem></SelectContent>
            </Select>
          </div>
        </div>}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="bio-qa-save">{busy ? "Saving…" : "Add employee"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MarkLeftDialog({ row, employees, onClose, onDone }) {
  const [leftOn, setLeftOn] = useState("");
  const [empId, setEmpId] = useState("");
  const [busy, setBusy] = useState(false);
  const [bid, setBid] = useState(null);
  if (row && bid !== row.biometric_id) { setBid(row.biometric_id); setLeftOn(row.last_punch || ""); setEmpId(""); }
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/attendance/biometric/mark-left", { biometric_id: row.biometric_id, name: row.biometric_name, left_on: leftOn || null, employee_id: empId || null });
      toast.success(`${row.biometric_name} marked as left`);
      onDone(row.biometric_id);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="bio-left-dialog">
        <DialogHeader>
          <DialogTitle>{row?.biometric_name} has left</DialogTitle>
          <DialogDescription>Device ID #{row?.biometric_id} won't appear as unmatched again. {row?.last_punch ? `Last punch: ${fmtDateIN(row.last_punch)}.` : "No punches this month."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Last working day</Label><DateInput value={leftOn} onChange={(e) => setLeftOn(e.target.value)} data-testid="bio-left-date"/></div>
          <div><Label className="text-xs">Also exit this SaarOne employee (optional)</Label>
            <EmployeePicker options={employees} value={empId} onChange={setEmpId} testid="bio-left-emp"/>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="bio-left-save">Mark as left</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UnmatchedActions({ onAdd, onLeft, id }) {
  return (
    <div className="flex gap-1 shrink-0">
      <Button size="sm" variant="outline" className="h-8 gap-1" onClick={onAdd} data-testid={`bio-add-${id}`}><UserPlus size={13}/> Add new</Button>
      <Button size="sm" variant="ghost" className="h-8 gap-1 text-zinc-600" onClick={onLeft} data-testid={`bio-left-${id}`}><SignOut size={13}/> Left</Button>
    </div>
  );
}
